import {useLocation, useNavigate} from "react-router-dom";
import {Alert, Center, CloseButton, Container, Group, Paper, Space, Text, Title} from "@mantine/core";
import {
  getRequiredObjectId,
  SiteAssignmentCompanyAssoc,
  SiteAssignmentTemplate,
  SiteAssignmentType,
  useCreateBulkSiteAssignmentMutation
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback} from "react";
import {notifications} from "@mantine/notifications";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {
  CompanySiteAssignmentBulkForm,
  CompanySiteAssignmentBulkFormValues
} from "../../components/form/site-assignment/CompanySiteAssignmentBulkForm/CompanySiteAssignmentBulkForm";
import {modals} from "@mantine/modals";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";
import {IconAlertCircle} from "@tabler/icons-react";

type Props = {
  type: SiteAssignmentType;
  formDefaults?: Partial<CompanySiteAssignmentBulkFormValues>
}

export const CompanySiteAssignmentBulkFormPageRoute = () => {
  const {state} = useLocation();
  const navigate = useNavigate()

  const type = state?.type
  const formDefaults = state?.formDefaults;

  if (!type) {
    navigate(-1);
    return (
      <ErrorPage/>
    );
  }

  return (
    <CompanySiteAssignmentBulkFormPage
      formDefaults={formDefaults}
      type={type}/>
  );
};

export const CompanySiteAssignmentBulkFormPage = (props: Props) => {
  const {formDefaults, type} = props;

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);
  const {regionId, appCategory} = useApp()

  const [createBulkSiteAssignment, {isLoading: isCreating}] = useCreateBulkSiteAssignmentMutation();

  const onSubmitConfirm = useCallback((formValues: CompanySiteAssignmentBulkFormValues) => {
    if (regionId) {

      const companyAssocs: SiteAssignmentCompanyAssoc[] = formValues.assocs.map((s) => {
        return {
          id: "",
          siteAssignmentId: "",
          companyId: getRequiredObjectId(s.company),
          normalPreferenceRank: s.normalRank,
          servicePreferenceRank: s.serviceRank,
        }
      });

      const template: SiteAssignmentTemplate = {
        regionId: regionId,
        objectIds: formValues.entityIds,
        objectType: formValues.entityType,
        template: {
          strategy: formValues.strategy?.code,
          type: type?.code,
          category: formValues.category?.code,
          companyAssocs: companyAssocs,
        }
      }
      createBulkSiteAssignment(template)
        .unwrap()
        .then(() => {
          notifications.show({
            color: "green",
            title: t("common.entity.siteassignment.company.plural", "Company Assignments", {ns: "common"}) + " " + t("common.status.created", "Created", {ns: "common"}),
            message: ""
          });
          navigate(-1);
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: t("common.error", "Error", {ns: "common"}),
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    }
  }, [regionId, type?.code, createBulkSiteAssignment, t, navigate]);

  const onSiteAssignmentSubmit = useCallback((formValues: CompanySiteAssignmentBulkFormValues) => {
    modals.openConfirmModal({
      title: t("common.action.create", "Create") + " " + t("common.entity.siteassignment.company.plural", "Company Assignments"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("page.siteassignment.company.bulkform.confirm", "Are you sure you want to bulk create company assignments? The company assignments will be applied to all matching sites.")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.submit", "Submit"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      onConfirm: () => {
        onSubmitConfirm(formValues)
      },
    })
  }, [onSubmitConfirm, t])

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            <Paper w="100%" shadow="sm" p="md" py="md">
              <Group position="apart">
                <Title align="center">
                  {t("page.siteassignment.company.bulkform.title.edit", "Bulk Company Assignment", {ns: "common"})}
                </Title>
                <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
              </Group>
              <Alert icon={<IconAlertCircle size="1rem"/>} title="Warning" color="yellow" m="sm" radius="lg">
                This may change existing company assignments for multiple sites and is not reversible.
              </Alert>
              <Space h="md"/>
              <CompanySiteAssignmentBulkForm
                options={{
                  entitySelectionDisabled: typeof formDefaults?.entityIds !== 'undefined' && typeof formDefaults?.entityType !== 'undefined',
                  categorySelectionDisabled: typeof formDefaults?.category !== 'undefined',
                }}
                defaultValues={{
                  ...(formDefaults ?? {}),
                  category: formDefaults?.category ?? appCategory
                }}
                submitting={isCreating}
                onSubmit={onSiteAssignmentSubmit}/>
            </Paper>
          </Center>
        </Container>
      )}
    </ThemedPage>
  );
};

